/** Settings → Accounts: connect YouTube channels (OAuth round trip), their health, and the YouTube-wide settings (audit, category, AI disclosure). */
import { useEffect, useRef, useState } from "react";
import { Link, navigate, useLocation } from "../../app/router";
import type { Account, AccountImpact, Category } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Badge, Button, Card, ConfirmDialog, EmptyState, ErrorNote, Field, Modal, PageHeader, QueryView, Switch } from "../../components/ui";
import { useApi, useMutation, useQuery } from "../../data/hooks";
import { useAccounts, useCategories, useLanguages, useYoutubeSettings } from "../../data/queries";
import { dateTime, languageName, relative } from "../../lib/format";
import type { Tone } from "../../lib/status";
import { AUDIT_FORM_URL, platformName } from "../publish/pubState";
import { AccountYoutubeData, DISCONNECT_DATA_NOTE } from "./YoutubeData";

export const CONNECT_ERRORS: Record<string, string> = {
  scopes: "Google didn't grant every permission ContentOps needs. Connect again and leave all the boxes ticked.",
  channel_in_use: "That YouTube channel is already connected to another account here. Pick a different channel.",
  access_denied: "The Google sign-in was cancelled. Nothing changed.",
  channel_mismatch: "You picked a different YouTube channel than the one this account publishes to, so nothing changed.",
};
const GENERIC_ERROR = "Couldn't connect the account. Try again.";

export function accountStatus(a: Account): { label: string; tone: Tone } {
  if (a.auth_status === "connected") return { label: "Connected", tone: "ok" };
  if (a.auth_status === "expiring") return { label: "Connected · renew soon", tone: "attention" };
  if (a.auth_status === "auth_required" || a.auth_status === "revoked") return { label: "Reconnect needed", tone: "danger" };
  return { label: "Disconnected", tone: "neutral" };
}

/** YouTube category ids offered as the default (videoCategories.list, US region; the adapter re-validates). */
export const YT_CATEGORIES: { id: string; name: string }[] = [
  { id: "27", name: "Education" }, { id: "22", name: "People & Blogs" }, { id: "29", name: "Nonprofits & Activism" },
  { id: "28", name: "Science & Technology" }, { id: "26", name: "Howto & Style" }, { id: "25", name: "News & Politics" }, { id: "24", name: "Entertainment" },
];

/** Reads ?connected= / ?error= once (the OAuth callback lands here), shows a toast, and strips the query. */
function useConnectReturn(accounts: Account[] | undefined, onChannelMismatch: (accountId: string) => void) {
  const { search } = useLocation();
  const toast = useToast();
  const connected = search.get("connected");
  const error = search.get("error");
  const account = search.get("account");
  const handled = useRef<string | null>(null); // StrictMode runs effects twice; toast once per return
  useEffect(() => {
    if (!connected && !error) return;
    if (connected && !accounts) return; // wait for the list so the toast can name the channel
    const key = `${connected}|${error}`;
    if (handled.current === key) return;
    handled.current = key;
    if (error === "channel_mismatch" && account) onChannelMismatch(account);
    else if (error) toast.show({ tone: "danger", text: CONNECT_ERRORS[error] ?? GENERIC_ERROR });
    else {
      const a = accounts?.find((x) => x.id === connected);
      toast.ok(a ? `Connected ${a.profile.title ?? a.name}.` : "Account connected.");
    }
    navigate(`/settings/accounts${window.location.hash}`, { replace: true });
  }, [connected, error, account, accounts, toast, onChannelMismatch]);
}

export function AccountsScreen() {
  const q = useAccounts();
  const [mismatch, setMismatch] = useState<string | null>(null);
  useConnectReturn(q.data, setMismatch);
  const [adding, setAdding] = useState(false);
  return (
    <div className="stack-lg">
      <PageHeader back={<Link to="/settings" className="back-link"><Icon name="back" size={18} /> Settings</Link>} title="Accounts"
        subtitle="The channels ContentOps posts to. One sign-in per channel."
        actions={!adding && <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add</Button>} />
      {adding && <AddAccount onDone={() => setAdding(false)} />}
      <QueryView q={q}>{(items) => items.length ? (
        <ul className="cards" aria-label="Accounts">{items.map((a) => <li key={a.id}><AccountCard a={a} /></li>)}</ul>
      ) : !adding && <EmptyState icon="publish" title="No accounts yet" action={<Button variant="secondary" onClick={() => setAdding(true)}>Add a YouTube account</Button>}>Add one, then connect it to a YouTube channel.</EmptyState>}</QueryView>
      <YoutubeSettingsCard />
      {mismatch && <ChannelMismatch accountId={mismatch} name={q.data?.find((a) => a.id === mismatch)?.name} onClose={() => setMismatch(null)} />}
    </div>
  );
}

/**
 * The owner picked a different channel at Google than the one this account publishes to (?error=channel_mismatch&account=).
 * Nothing changed. He can reconnect and pick the original, or confirm the switch (?confirm_channel_change=1) — which holds every
 * post queued for this account, because each was planned for the old channel.
 */
export function ChannelMismatch({ accountId, name, onClose }: { accountId: string; name?: string; onClose: () => void }) {
  const api = useApi();
  return (
    <Modal title="That's a different YouTube channel" onClose={onClose} footer={
      <>
        <Button variant="ghost" onClick={onClose}>Keep as it is</Button>
        <Button variant="secondary" onClick={() => api.connectAccount(accountId)}>Reconnect the original</Button>
        <Button variant="danger" onClick={() => api.connectAccount(accountId, { confirmChannelChange: true })}>Switch this account to the new channel</Button>
      </>
    }>
      <p className="impact">{CONNECT_ERRORS.channel_mismatch} {name ? <>“{name}” still publishes to its original channel.</> : null}</p>
      <p className="audit-alert small" role="alert"><Icon name="alert" size={18} /> If you switch, every post queued for this account is <b>held</b> until you review it, because each was planned for the old channel. Videos already on the old channel stay there.</p>
    </Modal>
  );
}

/** Before disconnect / disable: what still goes live on YouTube (can't be stopped from here once we let go) and what will be held. */
function ImpactNote({ accountId, impact }: { accountId: string; impact: AccountImpact }) {
  const live = impact.live_scheduled_on_platform.count;
  return (
    <div className="stack">
      {live > 0 && (
        <p className="audit-alert small" role="alert"><Icon name="alert" size={18} />
          <span><b>{live} post{live === 1 ? " is" : "s are"} already scheduled on YouTube and will still go live</b> — cancel {live === 1 ? "it" : "them"} first? <Link to={`/publish?tab=history&account=${encodeURIComponent(accountId)}&state=scheduled,publishing`} className="text-link">See {live === 1 ? "it" : "them"}</Link></span>
        </p>
      )}
      {impact.pending_uploads > 0 && <p className="small">{impact.pending_uploads} post{impact.pending_uploads === 1 ? "" : "s"} not uploaded yet will be held until the account is back.</p>}
      {live === 0 && impact.pending_uploads === 0 && <p className="small muted">Nothing is scheduled on this account.</p>}
    </div>
  );
}

export const categoryName = (slug: string | null | undefined, cats: Category[]) => (slug ? cats.find((c) => c.slug === slug || c.name.toLowerCase() === slug.toLowerCase())?.name ?? slug : null);

function AccountCard({ a }: { a: Account }) {
  const api = useApi();
  const cats = useCategories().data ?? [];
  const toast = useToast();
  const [confirm, setConfirm] = useState<"disconnect" | "disable" | null>(null);
  // Always ask the API what is at stake before letting go of a channel (GET /api/accounts/:id/impact).
  const impact = useQuery(confirm ? `accounts/${a.id}/impact` : null, () => api.accountImpact(a.id), { staleMs: 0 });
  const disconnect = useMutation(() => api.disconnectAccount(a.id), { invalidate: ["accounts", "dashboard", "publications", "analytics"], onSuccess: () => { toast.ok(`Disconnected ${a.name}.`); setConfirm(null); }, onError: (e) => toast.error(e) });
  const enable = useMutation((enabled: boolean) => api.updateAccount(a.id, { enabled }), { invalidate: ["accounts", "dashboard", "publications"], onSuccess: (_r) => { setConfirm(null); toast.ok(a.enabled ? `Disabled ${a.name}.` : `Enabled ${a.name}.`); }, onError: (e) => toast.error(e) });
  const st = accountStatus(a);
  const needsConnect = a.auth_status !== "connected" && a.auth_status !== "expiring";
  const channel = a.profile.title ?? (a.external_id ? "YouTube channel" : null);
  return (
    <article className={`pub tone-${st.tone}`} aria-label={a.name}>
      <div className="acct-head">
        {a.profile.thumbnail_url ? <img className="acct-thumb" src={a.profile.thumbnail_url} alt="" width={44} height={44} /> : <span className="acct-thumb acct-thumb-empty"><Icon name="user" size={22} /></span>}
        <span className="list-main">
          <span className="list-title">{a.name}</span>
          <span className="list-sub">{[platformName(a.platform), categoryName(a.category, cats), a.language_code ? languageName(a.language_code) : null].filter(Boolean).join(" · ")}</span>
        </span>
        <Badge tone={st.tone}>{st.label}</Badge>
      </div>
      {channel && <p className="small">Channel: <b>{channel}</b>{a.profile.handle ? <span className="muted"> {a.profile.handle}</span> : null}</p>}
      {a.token_expires_at && !needsConnect && <p className="muted small">Access token {Date.parse(a.token_expires_at) > Date.now() ? "renews" : "renewed"} {relative(a.token_expires_at)} · renews automatically</p>}
      {a.last_error && needsConnect && <p className="pub-error"><Icon name="alert" size={18} /> {a.last_error.message} <span className="muted">({dateTime(a.last_error.at)})</span></p>}
      {!needsConnect && a.platform === "youtube" && a.analytics_enabled === false && (
        <p className="audit-alert small" role="status"><Icon name="lock" size={18} />
          <span>Analytics not granted — <button type="button" className="text-link inline" onClick={() => api.connectAccount(a.id)} aria-label="Reconnect to grant analytics">reconnect</button> and allow YouTube Analytics.</span>
        </p>
      )}
      <div className="pub-actions">
        {needsConnect
          ? <Button size="sm" variant="primary" icon="lock" onClick={() => api.connectAccount(a.id)}>{a.auth_status === "disconnected" ? "Connect" : "Reconnect"}</Button>
          : <Button size="sm" variant="ghost" onClick={() => setConfirm("disconnect")}>Disconnect</Button>}
        {a.enabled
          ? <Button size="sm" variant="ghost" onClick={() => setConfirm("disable")}>Disable</Button>
          : <Button size="sm" variant="ghost" busy={enable.pending} onClick={() => void enable.mutate(true)}>Enable</Button>}
      </div>
      {!a.enabled && <p className="muted small">Disabled: nothing uploads to this account until you enable it.</p>}
      <AccountYoutubeData a={a} />
      {confirm && (
        <ConfirmDialog title={confirm === "disconnect" ? `Disconnect ${a.name}?` : `Disable ${a.name}?`} confirmLabel={confirm === "disconnect" ? "Disconnect" : "Disable"}
          busy={disconnect.pending || enable.pending || impact.isLoading}
          impact={confirm === "disconnect"
            ? `ContentOps signs out of this channel at Google and forgets its keys. Posts that aren't uploaded yet wait with “Reconnect needed” until you connect again. Videos already on YouTube are untouched. ${DISCONNECT_DATA_NOTE}`
            : "Nothing uploads to this account while it's disabled; posts whose time passes are held for you to reschedule."}
          onCancel={() => setConfirm(null)} onConfirm={() => void (confirm === "disconnect" ? disconnect.mutate() : enable.mutate(false))}>
          {impact.data ? <ImpactNote accountId={a.id} impact={impact.data} /> : impact.error ? <ErrorNote error={impact.error} /> : <p className="muted small">Checking what's scheduled…</p>}
        </ConfirmDialog>
      )}
    </article>
  );
}

function AddAccount({ onDone }: { onDone: () => void }) {
  const api = useApi();
  const toast = useToast();
  const cats = useCategories().data ?? [];
  const langs = useLanguages().data ?? [];
  const [form, setForm] = useState({ name: "", category: "", language_code: "en" });
  const category = form.category || cats[0]?.slug || "";
  const create = useMutation(() => api.createAccount({ platform: "youtube", name: form.name.trim(), category, language_code: form.language_code }), {
    invalidate: ["accounts"], onSuccess: (a) => { toast.ok(`Added ${a.name}. Connect it to a channel next.`); onDone(); }, onError: (e) => toast.error(e, "Couldn't add"),
  });
  return (
    <Card title="Add an account">
      <form className="stack" onSubmit={(e) => { e.preventDefault(); if (form.name.trim()) void create.mutate(); }}>
        <Field label="Platform">{(id) => <select id={id} value="youtube" disabled><option value="youtube">YouTube</option></select>}</Field>
        <Field label="Name" hint="For you, e.g. “Money Moves · English”">{(id, d) => <input id={id} aria-describedby={d} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="off" required />}</Field>
        <div className="grid-2">
          <Field label="Category">{(id) => (
            <select id={id} value={category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          )}</Field>
          <Field label="Language">{(id) => (
            <select id={id} value={form.language_code} onChange={(e) => setForm({ ...form, language_code: e.target.value })}>
              {(langs.length ? langs : [{ code: "en", name: "English" }]).map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          )}</Field>
        </div>
        <div className="row"><Button variant="ghost" onClick={onDone}>Cancel</Button><Button type="submit" variant="primary" busy={create.pending} disabled={!form.name.trim() || !category}>Add account</Button></div>
      </form>
    </Card>
  );
}

function YoutubeSettingsCard() {
  const api = useApi();
  const toast = useToast();
  const q = useYoutubeSettings();
  const save = useMutation((patch: Parameters<typeof api.updateYoutubeSettings>[0]) => api.updateYoutubeSettings(patch), { invalidate: ["settings/youtube"], onSuccess: () => toast.ok("Saved."), onError: (e) => toast.error(e, "Couldn't save") });
  return (
    <Card title="YouTube settings" id="youtube">
      <QueryView q={q}>{(s) => (
        <div className="stack">
          <Switch label="API audit passed" checked={s.api_audited} disabled={save.pending} onChange={(v) => void save.mutate({ api_audited: v })}
            hint={<>Until Google audits this app, every video it uploads is locked <b>private</b> — it can't be made public later and would have to be uploaded again. So nothing uploads while this is off; scheduled posts wait. Turn it on only after Google confirms the audit. <a className="text-link" href={AUDIT_FORM_URL} target="_blank" rel="noreferrer">Audit request form</a></>} />
          {!s.api_audited && <p className="audit-alert small" role="status"><Icon name="lock" size={18} /> Audit not passed yet — uploads are on hold.</p>}
          <Field label="Default YouTube category" hint="Used when a video doesn't set its own.">{(id, d) => (
            <select id={id} aria-describedby={d} value={s.default_category_id} disabled={save.pending} onChange={(e) => void save.mutate({ default_category_id: e.target.value })}>
              {[...YT_CATEGORIES, ...(YT_CATEGORIES.some((c) => c.id === s.default_category_id) ? [] : [{ id: s.default_category_id, name: `Category ${s.default_category_id}` }])].map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}</Field>
          <Switch label="Label videos as AI-altered" checked={s.contains_synthetic_media} disabled={save.pending} onChange={(v) => void save.mutate({ contains_synthetic_media: v })}
            hint="On by default: our videos use realistic AI scenes and AI-composed music, which YouTube asks creators to disclose. It doesn't limit reach or monetisation. You can still turn it off for a single, fully stylised video." />
        </div>
      )}</QueryView>
    </Card>
  );
}
